// src/components/store/sections/product-cards/BentoSplitProductCard.tsx
"use client";

import Image from "next/image";
import Link from "next/link";
import { Star, ShoppingCart, ShieldCheck, Check, Flame } from "lucide-react";
import { UniversalProductCardProps, useCardActions } from "./useCardActions";

const BENTO_THEMES = [
  { tileBg: "bg-indigo-50 border-indigo-100 text-indigo-700", ctaBg: "bg-indigo-600 hover:bg-indigo-700" },
  { tileBg: "bg-rose-50 border-rose-100 text-rose-700", ctaBg: "bg-rose-600 hover:bg-rose-700" },
  { tileBg: "bg-emerald-50 border-emerald-100 text-emerald-700", ctaBg: "bg-emerald-600 hover:bg-emerald-700" },
  { tileBg: "bg-amber-50 border-amber-100 text-amber-800", ctaBg: "bg-amber-600 hover:bg-amber-700" },
];

export default function BentoSplitProductCard({
  product,
  index = 0,
  subdomain = "nextlook",
}: UniversalProductCardProps) {
  const {
    productImg,
    productName,
    productUrl,
    categoryName,
    rating,
    currentPrice,
    discountPercent,
    added,
    handleAddToCart,
  } = useCardActions(product, subdomain);

  const theme = BENTO_THEMES[index % BENTO_THEMES.length];

  return (
    <div className="w-full h-full bg-slate-100/80 rounded-[28px] p-2.5 border border-slate-200/80 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between gap-2 group select-none">
      {/* Top Bento Row: Left Image Cell + Right Vertical Mini Tiles */}
      <div className="grid grid-cols-12 gap-2">
        <Link
          href={productUrl}
          className="col-span-8 relative h-44 rounded-2xl bg-white border border-slate-200/60 flex items-center justify-center p-2 overflow-hidden"
        >
          <Image
            src={productImg}
            alt={productName}
            fill
            unoptimized
            className="object-contain p-2 group-hover:scale-110 transition-transform duration-500"
          />
        </Link>

        <div className="col-span-4 flex flex-col gap-2">
          {/* Discount Tile */}
          <div className={`flex-1 rounded-2xl border p-2 flex flex-col items-center justify-center text-center ${theme.tileBg}`}>
            <Flame className="w-4 h-4 fill-current mb-0.5" />
            <span className="text-xs font-black leading-none">-{discountPercent}%</span>
            <span className="text-[8px] font-bold uppercase opacity-75 mt-0.5">SAVINGS</span>
          </div>

          {/* Rating Tile */}
          <div className="flex-1 rounded-2xl bg-white border border-slate-200/60 p-2 flex flex-col items-center justify-center text-center">
            <Star className="w-4 h-4 fill-amber-400 text-amber-400 mb-0.5" />
            <span className="text-xs font-black text-slate-800 leading-none">{rating.toFixed(1)}</span>
            <span className="text-[8px] font-bold text-slate-400 uppercase mt-0.5">RATED</span>
          </div>
        </div>
      </div>

      {/* Middle Bento Cell: Title & Category */}
      <div className="rounded-2xl bg-white border border-slate-200/60 p-3 space-y-1">
        <div className="flex items-center justify-between text-[9px] font-black uppercase tracking-wider text-slate-400">
          <span className="truncate max-w-[130px]">{categoryName}</span>
          <span className="flex items-center gap-0.5 text-emerald-600">
            <ShieldCheck className="w-3 h-3" /> Verified
          </span>
        </div>

        <Link href={productUrl} className="block">
          <h3 className="text-xs sm:text-sm font-black text-slate-900 truncate hover:underline">
            {productName}
          </h3>
        </Link>
      </div>

      {/* Bottom Bento Cell: Price & Action */}
      <div className="rounded-2xl bg-slate-900 text-white p-3 flex items-center justify-between gap-2">
        <div>
          <span className="text-[8px] font-mono uppercase text-slate-400 block leading-none">
            BENTO PRICE
          </span>
          <span className="text-base font-black font-mono text-white">
            ৳{currentPrice.toLocaleString()}
          </span>
        </div>

        <button
          type="button"
          onClick={(e) => handleAddToCart(e)}
          className={`px-3.5 py-2 rounded-xl ${added ? "bg-emerald-500" : theme.ctaBg} text-white font-black text-[10px] uppercase tracking-wider shadow-sm active:scale-95 transition-all cursor-pointer flex items-center gap-1`}
        >
          {added ? <Check className="w-3.5 h-3.5" /> : <ShoppingCart className="w-3.5 h-3.5" />}
          <span>{added ? "Added" : "Add"}</span>
        </button>
      </div>
    </div>
  );
}
